/**
 * Patient lifecycle display constants (Phase 6: intake → admission → hold → discharge).
 * Wire values come from the DTOs; this file only holds UI labels and the allowed
 * referral transitions mirrored from the backend `referral` state machine (§8).
 */

import type { DischargeReasonCode, HoldReasonCode, PatientStatus, PatientStatusWire } from '../dtos/admin/patient.dto';
import type { DeclineReason, ReferralStatus } from '../dtos/clinical/referral.dto';
import type { LegalAuthority } from '../dtos/patientmr/legalGuardian.dto';
import type { AllergySeverity, DmeEquipmentCode } from '../dtos/patientmr/demographics.dto';

export interface LabeledOption<T extends string> {
  value: T;
  label: string;
}

// ─── Patient status ───────────────────────────────────────────────────────────

export const PATIENT_STATUS_LABELS: Record<PatientStatus, string> = {
  PENDING_ADMISSION: 'Pending',
  ACTIVE: 'Active',
  ON_HOLD: 'On Hold',
  INACTIVE: 'Inactive',
};

/** Patient list filter chips (PAT-08). Order is the display order. */
export const PATIENT_STATUS_FILTERS: LabeledOption<PatientStatusWire>[] = [
  { value: 'active', label: 'Active' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'pending_admission', label: 'Pending' },
  { value: 'inactive', label: 'Inactive' },
];

/** The census excludes discharged (inactive) patients by default (DSC-08). */
export const DEFAULT_PATIENT_STATUS_FILTER: PatientStatusWire[] = ['active', 'on_hold', 'pending_admission'];

// ─── Hold ─────────────────────────────────────────────────────────────────────

export const HOLD_REASON_OPTIONS: LabeledOption<HoldReasonCode>[] = [
  { value: 'HOSPITALIZED', label: 'Hospitalized' },
  { value: 'FAMILY_REQUEST', label: 'Family request' },
  { value: 'INSURANCE_ISSUE', label: 'Insurance issue' },
  { value: 'STAFFING_UNAVAILABLE', label: 'Staffing unavailable' },
  { value: 'OTHER', label: 'Other' },
];

/** Hold older than this many days with an active PA shows the PA-review banner (HLD-05). */
export const HOLD_PA_REVIEW_DAYS = 14;

// ─── Discharge ────────────────────────────────────────────────────────────────

export const DISCHARGE_REASON_OPTIONS: LabeledOption<DischargeReasonCode>[] = [
  { value: 'GOALS_MET', label: 'Goals met' },
  { value: 'AGED_OUT', label: 'Aged out' },
  { value: 'FAMILY_REQUEST', label: 'Family request' },
  { value: 'PATIENT_DECLINED', label: 'Patient declined services' },
  { value: 'LONG_TERM_FACILITY', label: 'Moved to a long-term facility' },
  { value: 'SAFETY', label: 'Safety concern' },
  { value: 'HOSPITALIZED', label: 'Hospitalized' },
  { value: 'TRANSFERRED', label: 'Transferred to another agency' },
  { value: 'MOVED', label: 'Moved out of service area' },
  { value: 'PAYER_TERMINATED', label: 'Payer terminated coverage' },
  { value: 'DECEASED', label: 'Deceased' },
  { value: 'OTHER', label: 'Other' },
];

/** Age (years) at which AGED_OUT is pre-selected in the discharge dialog (DSC-09). */
export const AGED_OUT_AGE = 21;

/** Readable label for a stored discharge reason, including the system-only REFERRAL_DECLINED. */
export function dischargeReasonLabel(reason: string | null | undefined): string {
  if (!reason) return 'Reason not recorded';
  if (reason === 'REFERRAL_DECLINED') return 'Referral declined';
  return DISCHARGE_REASON_OPTIONS.find((o) => o.value === reason)?.label ?? reason;
}

export function holdReasonLabel(reason: string | null | undefined): string {
  if (!reason) return 'Reason not recorded';
  return HOLD_REASON_OPTIONS.find((o) => o.value === reason)?.label ?? reason;
}

// ─── Referrals ────────────────────────────────────────────────────────────────

export const REFERRAL_STATUS_LABELS: Record<ReferralStatus, string> = {
  RECEIVED: 'Received',
  IN_REVIEW: 'In review',
  ACCEPTED: 'Accepted',
  SCHEDULED: 'Scheduled',
  DECLINED: 'Declined',
};

/** Allowed next statuses per the §8 referral transition table. SCHEDULED and DECLINED are terminal. */
export const REFERRAL_TRANSITIONS: Record<ReferralStatus, ReferralStatus[]> = {
  RECEIVED: ['IN_REVIEW', 'ACCEPTED', 'DECLINED'],
  IN_REVIEW: ['ACCEPTED', 'DECLINED'],
  ACCEPTED: ['SCHEDULED', 'DECLINED'],
  SCHEDULED: [],
  DECLINED: [],
};

/** Normalize a stored referral status (REVIEWING is a legacy alias of IN_REVIEW). */
export function normalizeReferralStatus(status: string | null | undefined): ReferralStatus {
  const upper = String(status || 'RECEIVED').trim().toUpperCase().replace(/[\s-]+/g, '_');
  if (upper === 'REVIEWING') return 'IN_REVIEW';
  return (Object.keys(REFERRAL_TRANSITIONS) as ReferralStatus[]).includes(upper as ReferralStatus)
    ? (upper as ReferralStatus)
    : 'RECEIVED';
}

export const DECLINE_REASON_OPTIONS: LabeledOption<DeclineReason>[] = [
  { value: 'NO_STAFF_CAPACITY', label: 'No staff capacity' },
  { value: 'OUTSIDE_SERVICE_AREA', label: 'Outside service area' },
  { value: 'PAYER_NOT_ACCEPTED', label: 'Payer not accepted' },
  { value: 'CLINICALLY_NOT_A_FIT', label: 'Clinically not a fit' },
  { value: 'FAMILY_DECLINED', label: 'Family declined' },
  { value: 'DUPLICATE', label: 'Duplicate' },
  { value: 'OTHER', label: 'Other' },
];

export const REFERRAL_SOURCE_OPTIONS = ['Hospital', 'Physician', 'MCO', 'Family', 'Other'] as const;

export const MISSING_INFO_LABELS: Record<string, string> = {
  physician: 'Referring physician',
};

// ─── Registration ─────────────────────────────────────────────────────────────

export const LEGAL_AUTHORITY_OPTIONS: LabeledOption<LegalAuthority>[] = [
  { value: 'PARENT', label: 'Parent' },
  { value: 'LEGAL_GUARDIAN', label: 'Legal guardian' },
  { value: 'CONSERVATOR', label: 'Conservator' },
  { value: 'OTHER', label: 'Other' },
];

export const ALLERGY_SEVERITY_OPTIONS: LabeledOption<AllergySeverity>[] = [
  { value: 'MILD', label: 'Mild' },
  { value: 'MODERATE', label: 'Moderate' },
  { value: 'SEVERE', label: 'Severe' },
];

export const DME_EQUIPMENT_OPTIONS: LabeledOption<DmeEquipmentCode>[] = [
  { value: 'VENTILATOR', label: 'Ventilator' },
  { value: 'OXYGEN', label: 'Oxygen' },
  { value: 'SUCTION', label: 'Suction' },
  { value: 'FEEDING_PUMP', label: 'Feeding pump' },
  { value: 'HOSPITAL_BED', label: 'Hospital bed' },
  { value: 'OTHER', label: 'Other' },
];
